import { useCallback, useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { mvs } from 'react-native-size-matters';

import { CustomIcon } from './CustomIcon';
import { voteTrack, voteTrackWithSlug } from '../api/managerApi';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { IS_WEB, isTablet } from '../constants';
import { selectRadioTab } from '../features/appconfig/store/selectors';
import { selectCurrentRadio } from '../features/currentradio/store/selectors';
import { selectCurrentTrack } from '../features/currenttrack/store/selectors';
import { showAlert } from '../features/modal/store/slice';
import i18n from '../localization/i18n';
import { VotePayloadModel } from '../models/Vote.model';

export const VoteButtons = () => {
  const radioTab = useAppSelector(selectRadioTab);
  const currentRadio = useAppSelector(selectCurrentRadio);
  const currentTrack = useAppSelector(selectCurrentTrack);
  const [voted, setVoted] = useState(false);
  const [voteChoice, setVoteChoice] = useState(0);
  const dispatch = useAppDispatch();

  useEffect(() => {
    setVoted(false);
    setVoteChoice(0);
  }, [currentTrack, currentRadio]);

  const onVote = useCallback(
    (vote: VotePayloadModel['vote']) => {
      if (!currentRadio?.urlFlux) {
        return;
      }
      if (currentRadio?.idradio) {
        setVoteChoice(vote);
        voteTrack(currentRadio?.idradio, vote)
          .then(() => {
            setVoted(true);
            dispatch(
              showAlert({
                key: 'vote-success',
                type: 'info',
                text: i18n.t('alert.voteSuccess'),
                closable: false,
                autoCloseAfterMs: 2000,
              }),
            );
          })
          .catch(err => {
            if (err?.response?.status === 429) {
              setVoted(true);
              dispatch(
                showAlert({
                  key: 'vote-duplicate',
                  type: 'info',
                  text: i18n.t('alert.voteDuplicate'),
                  closable: false,
                  autoCloseAfterMs: 2000,
                }),
              );
            } else {
              dispatch(
                showAlert({
                  key: 'vote-error',
                  type: 'warning',
                  text: i18n.t('alert.voteError'),
                  closable: false,
                  autoCloseAfterMs: 2000,
                }),
              );
            }
          });
      } else {
        const slug = currentRadio.urlFlux.substring(
          currentRadio.urlFlux.lastIndexOf('/') + 1,
        );
        setVoteChoice(vote);
        voteTrackWithSlug(slug, vote)
          .then(() => {
            setVoted(true);
            dispatch(
              showAlert({
                key: 'vote-success',
                type: 'info',
                text: i18n.t('alert.voteSuccess'),
                closable: false,
                autoCloseAfterMs: 2000,
              }),
            );
          })
          .catch(err => {
            if (err?.response?.status === 429) {
              setVoted(true);
              dispatch(
                showAlert({
                  key: 'vote-duplicate',
                  type: 'info',
                  text: i18n.t('alert.voteDuplicate'),
                  closable: false,
                  autoCloseAfterMs: 2000,
                }),
              );
            } else {
              dispatch(
                showAlert({
                  key: 'vote-error',
                  type: 'warning',
                  text: i18n.t('alert.voteError'),
                  closable: false,
                  autoCloseAfterMs: 2000,
                }),
              );
            }
          });
      }
    },
    [currentRadio, dispatch],
  );

  return (
    <View style={styles.container}>
      <CustomIcon
        name="like"
        color={radioTab?.titleLabelColor}
        size={mvs(32)}
        containerStyle={[
          styles.icon,
          voted && voteChoice === -1 ? styles.notChosen : null,
        ]}
        disabled={voted || IS_WEB}
        onPress={() => onVote(1)}
        accessibilityLabel={i18n.t('accessibility.like')}
        accessibilityRole="button"
      />
      <CustomIcon
        name="dislike"
        color={radioTab?.titleLabelColor}
        size={mvs(32)}
        containerStyle={[
          styles.icon,
          voted && voteChoice === 1 ? styles.notChosen : null,
        ]}
        disabled={voted || IS_WEB}
        onPress={() => onVote(-1)}
        accessibilityLabel={i18n.t('accessibility.dislike')}
        accessibilityRole="button"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    marginVertical: mvs(10),
  },
  icon: {
    marginHorizontal: isTablet ? mvs(16) : 40,
  },
  notChosen: {
    opacity: 0.5,
  },
});
